import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { getUserSettingsApi, UserSettingsDocument } from "./api";
import { useMsal } from "@azure/msal-react";
import { getToken } from "./authConfig";
import { useSettings } from "./SettingsContext";
import { useAuthContext } from "./loginContext";

interface UserSettingsContextType {
    userSettings: UserSettingsDocument["settings"] | undefined;
    userCountry: string;
    loading: boolean;
    error: string | null;
}

const UserSettingsContext = createContext<UserSettingsContextType>({} as UserSettingsContextType);

export const useUserSettings = () => useContext(UserSettingsContext);

export const UserSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [userCountry, setUserCountry] = useState("US");
    const [error, setError] = useState<string | null>(null);
    const { instance: client } = useMsal();
    const [settings, dispatchSettings] = useSettings();
    const [response, setResponse] = useState<UserSettingsDocument | null>(null);
    const { authLoading, isLoggedIn } = useAuthContext();
    const abortControllerRef = useRef<AbortController | null>(null);
    const fetchAttemptRef = useRef(0);

    const fetchUserSettings = async () => {
        // Don't fetch if auth is still loading
        if (authLoading) {
            return;
        }

        // Cancel any ongoing fetch
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        const currentAttempt = ++fetchAttemptRef.current;

        try {
            setLoading(true);
            setError(null);

            const idToken = await getToken(client);
            if (!idToken) {
                throw new Error("No authentication token available");
            }

            // Check if this fetch was cancelled
            if (controller.signal.aborted || currentAttempt !== fetchAttemptRef.current) {
                return;
            }

            const apiResult = await getUserSettingsApi(idToken);

            // Check again if this fetch was cancelled
            if (controller.signal.aborted || currentAttempt !== fetchAttemptRef.current) {
                return;
            }
            setResponse(apiResult);
            setUserCountry(apiResult.userCountry);

            // Apply values to local reducer if they exist
            Object.entries(apiResult.settings ?? {}).forEach(([key, value]) => {
                if (key in settings) {
                    dispatchSettings({
                        type: key as any,
                        value: parseSettingValue(settings[key as keyof typeof settings], value)
                    });
                }
            });

            setError(null);
        } catch (err: any) {
            // Only set error if this is still the current fetch
            if (!controller.signal.aborted && currentAttempt === fetchAttemptRef.current) {
                console.error("Error loading user settings:", err);
                setError(err.message || "Failed to load user settings");
            }
        } finally {
            // Only set loading to false if this is still the current fetch
            if (!controller.signal.aborted && currentAttempt === fetchAttemptRef.current) {
                setLoading(false);
            }
        }
    };

    const parseSettingValue = (localValue: any, serverValue: any): any => {
        if (typeof localValue === "boolean") return serverValue === "true";
        if (typeof localValue === "number") return parseFloat(serverValue);
        if (typeof localValue === "string") return String(serverValue);
        return serverValue;
    };

    useEffect(() => {
        if (!authLoading && isLoggedIn) {
            fetchUserSettings();
        } else if (!authLoading && !isLoggedIn) {
            // User is not logged in, clear data and stop loading
            setResponse(null);
            setError(null);
            setLoading(false);
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [client, authLoading, isLoggedIn]);

    return (
        <UserSettingsContext.Provider
            value={{
                userSettings: response?.settings,
                userCountry: userCountry,
                loading: loading || authLoading,
                error
            }}
        >
            {children}
        </UserSettingsContext.Provider>
    );
};
